import type { Snapshot } from '../shared/types';
import pLimit from 'p-limit';
const commandQueue = pLimit(1);
const sessionChanges = new Set([
  '/auth/sign-in/email',
  '/auth/sign-up/email',
  '/auth/sign-out',
  '/auth/reset-password',
  '/character',
]);
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
  }
}
export async function api<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.method &&
      options.method !== 'GET' &&
      !path.startsWith('/auth/') &&
      path !== '/character' &&
      sessionStorage.getItem('ado-character-id')
        ? { 'X-Character-ID': sessionStorage.getItem('ado-character-id')! }
        : {}),
      ...options.headers,
    },
    signal: options.signal || AbortSignal.timeout(20000),
  });
  const data = await response
    .json()
    .catch(() => ({ code: 'NETWORK', message: 'Unable to read the server response.' }));
  if (!response.ok)
    throw new ApiError(
      data.code || (response.status === 429 ? 'RATE_LIMIT' : 'REQUEST'),
      data.message || 'Request failed.',
      response.status,
    );
  if (options.method === 'POST' && sessionChanges.has(path) && typeof window !== 'undefined') {
    try {
      localStorage.setItem('ado-session-change', crypto.randomUUID());
    } catch {
      /* A storage restriction must not fail a committed sign-in. */
    }
  }
  return data;
}
export const post = <T = any>(path: string, body: unknown) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });
export function command(type: string, payload: Record<string, unknown> = {}) {
  if (commandQueue.pendingCount >= 20)
    return Promise.reject(new ApiError('COMMAND_BUSY', 'Please let the current actions finish.', 429));
  const expected = sessionStorage.getItem('ado-character-id');
  return commandQueue(() => {
    if (sessionStorage.getItem('ado-character-id') !== expected)
      throw new ApiError('SESSION_CHANGED', 'The signed-in account changed.', 409);
    return sendCommand(type, payload);
  });
}
async function sendCommand(type: string, payload: Record<string, unknown> = {}) {
  const pending = sessionStorage.getItem('ado-pending-command');
  if (pending) {
    try {
      const prior = JSON.parse(pending);
      await post('/commands', prior);
      sessionStorage.removeItem('ado-pending-command');
    } catch (e) {
      if (e instanceof SyntaxError || (e instanceof ApiError && e.status < 500))
        sessionStorage.removeItem('ado-pending-command');
      else throw e;
    }
  }
  const q = {
    requestId: crypto.randomUUID(),
    type,
    payload,
    characterId: sessionStorage.getItem('ado-character-id') || undefined,
  };
  sessionStorage.setItem('ado-pending-command', JSON.stringify(q));
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await post('/commands', q);
      sessionStorage.removeItem('ado-pending-command');
      return r;
    } catch (error) {
      if (error instanceof ApiError && error.status < 500) {
        sessionStorage.removeItem('ado-pending-command');
        throw error;
      }
      if (attempt === 1) throw error;
    }
  }
}
export const getWorld = () => api<Snapshot>('/world');
